/**
 * legal-content.ts — Nội dung ba văn bản pháp lý công khai của eCDP. Tách khỏi giao diện để kiểm bằng test
 * (mục bắt buộc của Meta App Review: Chính sách bảo mật, Điều khoản, Hướng dẫn xoá dữ liệu).
 *
 * Chỉ viết những gì hệ thống THẬT SỰ làm (đối chiếu mã nguồn `facebook-page/*`). Cam kết nào là quyết định
 * của doanh nghiệp (vd thời hạn xử lý yêu cầu xoá) thì có ghi chú ở nhật ký để chủ sản phẩm xác nhận.
 *
 * i18n-ignore-file — văn bản pháp lý (Điều khoản / Chính sách bảo mật / Xoá dữ liệu), có giá trị pháp lý theo
 * luật Việt Nam, KHÔNG dịch máy theo ngôn ngữ UI (rủi ro sai lệch nội dung pháp lý khi dịch tự động).
 */
import type { LegalInfo } from './legal-info'

export type LegalKind = 'terms' | 'privacy' | 'data-deletion' | 'claude-connector'

export interface LegalSection {
  heading: string
  /** Đoạn văn. */
  paragraphs?: string[]
  /** Danh sách gạch đầu dòng. */
  bullets?: string[]
  /** Danh sách đánh số (các bước). */
  steps?: string[]
}

export interface LegalDoc {
  title: string
  /** Mô tả ngắn dưới tiêu đề. */
  lead: string
  sections: LegalSection[]
}

/** Số ngày tối đa để xử lý yêu cầu xoá dữ liệu — một chỗ duy nhất, văn bản và test cùng đọc. */
export const DELETION_SLA_DAYS = 30

/** Đường dẫn công khai của từng văn bản (dùng làm URL khai với Meta). */
export const LEGAL_PATH: Record<LegalKind, string> = {
  terms: '/dieu-khoan',
  privacy: '/chinh-sach-bao-mat',
  'data-deletion': '/xoa-du-lieu',
  // Tài liệu công khai của connector MCP — URL khai ở danh bạ connector của Anthropic (docs/THIET-KE-MCP-CONNECTOR.md).
  'claude-connector': '/ket-noi-claude',
}

function operator(info: LegalInfo): string {
  if (!info.companyName) return 'đơn vị vận hành'
  const en = info.companyNameEn ? ` (${info.companyNameEn})` : ''
  const mst = info.taxId ? `, mã số thuế ${info.taxId}` : ''
  const addr = info.address ? `, trụ sở ${info.address}` : ''
  return `${info.companyName}${en}${mst}${addr}`
}

function contactLine(info: LegalInfo): string {
  const parts = [info.email && `email ${info.email}`, info.phone && `điện thoại ${info.phone}`].filter(Boolean)
  return parts.length > 0
    ? `Liên hệ ${info.product}: ${parts.join(', ')}.`
    : `Liên hệ ${info.product} qua mục Hỗ trợ trong ứng dụng.`
}

export function buildLegalDoc(kind: LegalKind, info: LegalInfo): LegalDoc {
  if (kind === 'privacy') return privacy(info)
  if (kind === 'terms') return terms(info)
  if (kind === 'claude-connector') return claudeConnector(info)
  return dataDeletion(info)
}

function privacy(info: LegalInfo): LegalDoc {
  const p = info.product
  return {
    title: 'Chính sách bảo mật',
    lead: `Cách ${p} thu thập, sử dụng, lưu trữ và bảo vệ dữ liệu, kể cả dữ liệu nhận từ Facebook Messenger. Hiệu lực từ ${info.effectiveDate}.`,
    sections: [
      {
        heading: '1. Chúng tôi là ai',
        paragraphs: [
          `${p} do ${operator(info)} vận hành. ${p} là nền tảng quản lý khách hàng và nhắn tin đa kênh dành cho doanh nghiệp: gom tin nhắn từ Zalo, Facebook Messenger, Live Chat website và các kênh khác về một hộp thư, quản lý hồ sơ khách hàng, phân công nhân viên và tự động hoá chăm sóc khách.`,
          'Chính sách này áp dụng cho website và ứng dụng của chúng tôi, và cho dữ liệu được xử lý khi một doanh nghiệp kết nối Trang Facebook (Fanpage) vào hệ thống.',
        ],
      },
      {
        heading: '2. Vai trò của chúng tôi',
        bullets: [
          `Với dữ liệu tài khoản của người dùng ${p} (nhân viên, quản trị doanh nghiệp): chúng tôi là bên kiểm soát dữ liệu.`,
          `Với dữ liệu của khách hàng nhắn tin vào các kênh của doanh nghiệp (ví dụ người nhắn tin vào Fanpage): doanh nghiệp sở hữu Trang là bên kiểm soát dữ liệu; ${p} xử lý dữ liệu đó thay mặt và theo chỉ dẫn của doanh nghiệp.`,
        ],
      },
      {
        heading: '3. Dữ liệu chúng tôi thu thập',
        paragraphs: ['Dữ liệu tài khoản và sử dụng:'],
        bullets: [
          'Họ tên, email, số điện thoại, mật khẩu (lưu dạng băm), vai trò và quyền trong tổ chức.',
          'Thông tin gói dịch vụ, đơn hàng thanh toán của tổ chức.',
          'Nhật ký kỹ thuật: địa chỉ IP, loại trình duyệt/thiết bị, thời điểm truy cập, lỗi hệ thống — để bảo mật và vận hành.',
        ],
      },
      {
        heading: '4. Dữ liệu nhận từ Facebook (Messenger)',
        paragraphs: [
          'Khi quản trị viên của một Trang Facebook bấm "Kết nối Fanpage" và cấp quyền, chúng tôi nhận và lưu:',
        ],
        bullets: [
          'Về Trang: mã Trang (Page ID), tên Trang và mã truy cập Trang (Page access token). Mã truy cập được mã hoá khi lưu.',
          'Về người nhắn tin vào Trang: mã định danh theo Trang (PSID), tên hiển thị và ảnh đại diện công khai do Facebook cung cấp.',
          'Về hội thoại: nội dung tin nhắn, tệp đính kèm (ảnh, tệp, âm thanh…), thời gian gửi, trạng thái đã gửi/đã đọc, nội dung bấm nút (postback).',
        ],
      },
      {
        heading: '5. Quyền truy cập Facebook mà chúng tôi xin và dùng để làm gì',
        bullets: [
          'pages_show_list — liệt kê các Trang mà quản trị viên quản lý để chọn Trang cần kết nối.',
          'pages_messaging — nhận tin nhắn gửi tới Trang và gửi tin trả lời từ Trang qua Messenger.',
          'pages_manage_metadata — đăng ký nhận sự kiện (webhook) của Trang để tin nhắn mới về hệ thống theo thời gian thực.',
          'public_profile — xác định người đã uỷ quyền kết nối.',
        ],
        paragraphs: [
          'Chúng tôi không xin và không đọc bài đăng, bình luận, danh sách bạn bè hay thông tin cá nhân khác của người dùng Facebook ngoài các mục nêu trên.',
        ],
      },
      {
        heading: '6. Mục đích sử dụng dữ liệu',
        bullets: [
          'Hiển thị hội thoại và cho nhân viên của doanh nghiệp trả lời khách trong hệ thống.',
          'Quản lý hồ sơ khách hàng, phân công người phụ trách, gắn nhãn, thống kê và báo cáo cho chính doanh nghiệp đó.',
          'Chạy tự động hoá và Nhân viên AI mà doanh nghiệp chủ động bật (xem mục 7).',
          'Bảo mật tài khoản, phát hiện lạm dụng, vận hành và cải thiện chất lượng dịch vụ.',
          'Đáp ứng yêu cầu của cơ quan nhà nước có thẩm quyền theo quy định pháp luật.',
        ],
        paragraphs: [
          'Chúng tôi KHÔNG bán dữ liệu, KHÔNG dùng dữ liệu nhận từ Facebook để quảng cáo hay lập hồ sơ cho mục đích khác ngoài dịch vụ cung cấp cho đúng doanh nghiệp đã kết nối Trang, và KHÔNG chuyển dữ liệu của doanh nghiệp này cho doanh nghiệp khác. Dữ liệu của mỗi tổ chức được tách riêng.',
        ],
      },
      {
        heading: '7. Chia sẻ dữ liệu',
        paragraphs: ['Chúng tôi chỉ chia sẻ dữ liệu trong các trường hợp sau:'],
        bullets: [
          'Nhà cung cấp hạ tầng và dịch vụ vận hành hệ thống (máy chủ, lưu trữ, gửi email) — chỉ để cung cấp dịch vụ cho chúng tôi, theo hợp đồng bảo mật.',
          'Nhà cung cấp mô hình AI — chỉ khi doanh nghiệp bật Nhân viên AI hoặc tính năng AI: phần nội dung hội thoại cần thiết được gửi đi để tạo câu trả lời/gợi ý.',
          'Claude (Anthropic) — chỉ khi doanh nghiệp bật "Kết nối AI (Claude)" và từng người dùng tự kết nối tài khoản Claude của mình (xem /ket-noi-claude): mỗi lần người dùng hỏi, Claude đọc hoặc tạo bản nháp dữ liệu CRM trong đúng phạm vi quyền của người đó; số điện thoại / email bị che trừ khi doanh nghiệp cho phép. Phần dữ liệu đó được xử lý theo điều khoản tài khoản Claude của người dùng. Người dùng và quản trị viên thu hồi kết nối được bất cứ lúc nào; mọi lần truy cập được ghi nhật ký.',
          'Cơ quan nhà nước có thẩm quyền, khi pháp luật yêu cầu.',
        ],
      },
      {
        heading: '8. Lưu trữ và bảo mật',
        bullets: [
          'Kết nối truyền dữ liệu qua HTTPS; mật khẩu lưu dạng băm; mã truy cập kênh (kể cả Page access token) được mã hoá khi lưu.',
          'Phân quyền theo vai trò; dữ liệu của mỗi tổ chức được cách ly với tổ chức khác.',
          'Sự kiện gửi từ Facebook về hệ thống được xác thực chữ ký trước khi xử lý.',
          'Dữ liệu được giữ trong thời gian tổ chức còn sử dụng dịch vụ và đến khi có yêu cầu xoá hợp lệ (mục 10), trừ phần pháp luật bắt buộc phải lưu.',
        ],
      },
      {
        heading: '9. Quyền của bạn',
        paragraphs: [
          'Theo Nghị định 13/2023/NĐ-CP về bảo vệ dữ liệu cá nhân, bạn có quyền được biết, đồng ý hoặc rút lại đồng ý, truy cập, chỉnh sửa, xoá dữ liệu, hạn chế hoặc phản đối việc xử lý, và khiếu nại. Nếu bạn là khách của một doanh nghiệp (người nhắn tin vào Fanpage), bạn có thể gửi yêu cầu trực tiếp cho doanh nghiệp đó hoặc cho chúng tôi theo mục liên hệ.',
        ],
      },
      {
        heading: '10. Xoá dữ liệu',
        paragraphs: [
          'Hướng dẫn chi tiết cách yêu cầu xoá dữ liệu có nguồn từ Facebook, và cách thu hồi quyền của ứng dụng, có tại trang Hướng dẫn xoá dữ liệu: /xoa-du-lieu.',
        ],
      },
      {
        heading: '11. Trẻ em',
        paragraphs: [
          `${p} là dịch vụ dành cho doanh nghiệp, không hướng tới người dưới 16 tuổi và chúng tôi không chủ đích thu thập dữ liệu của đối tượng này. Nếu phát hiện, chúng tôi sẽ xoá khi nhận được thông báo.`,
        ],
      },
      {
        heading: '12. Thay đổi chính sách',
        paragraphs: [
          'Khi có thay đổi quan trọng, chúng tôi sẽ cập nhật trang này và ghi rõ ngày hiệu lực mới. Việc tiếp tục sử dụng dịch vụ sau ngày hiệu lực được xem là chấp nhận chính sách đã cập nhật.',
        ],
      },
      { heading: '13. Liên hệ', paragraphs: [contactLine(info)] },
    ],
  }
}

function terms(info: LegalInfo): LegalDoc {
  const p = info.product
  return {
    title: 'Điều khoản sử dụng',
    lead: `Các điều khoản áp dụng khi doanh nghiệp và người dùng sử dụng ${p}. Hiệu lực từ ${info.effectiveDate}.`,
    sections: [
      {
        heading: '1. Chấp nhận điều khoản',
        paragraphs: [
          `Khi đăng ký, đăng nhập hoặc sử dụng ${p}, bạn xác nhận đã đọc, hiểu và đồng ý với Điều khoản này và Chính sách bảo mật (/chinh-sach-bao-mat). Nếu bạn dùng ${p} thay mặt một tổ chức, bạn cam đoan có thẩm quyền ràng buộc tổ chức đó.`,
        ],
      },
      {
        heading: '2. Dịch vụ',
        paragraphs: [
          `${p} cung cấp phần mềm quản lý khách hàng và nhắn tin đa kênh dạng dịch vụ trực tuyến (hộp thư chung, hồ sơ khách, phân công, tự động hoá, Nhân viên AI, báo cáo, tích hợp kênh và hệ thống bán hàng). Tính năng và giới hạn của từng gói được công bố trong ứng dụng và có thể được cập nhật.`,
        ],
      },
      {
        heading: '3. Tài khoản và bảo mật',
        bullets: [
          'Bạn cung cấp thông tin chính xác và chịu trách nhiệm giữ bí mật mật khẩu, phân quyền đúng người trong tổ chức.',
          'Mọi hoạt động dưới tài khoản của bạn được xem là do bạn thực hiện; hãy báo ngay cho chúng tôi nếu nghi ngờ bị truy cập trái phép.',
        ],
      },
      {
        heading: '4. Kết nối kênh bên thứ ba (Facebook, Zalo, TikTok Shop, Shopee…)',
        bullets: [
          'Bạn chỉ kết nối kênh/Trang mà bạn có quyền quản trị, và tự chịu trách nhiệm có đủ quyền để xử lý dữ liệu khách hàng của kênh đó.',
          'Bạn phải tuân thủ điều khoản và chính sách của từng nền tảng (ví dụ Điều khoản Nền tảng Meta, chính sách Messenger, quy định của Zalo). Nền tảng có thể giới hạn, tạm ngừng hoặc thu hồi quyền truy cập mà chúng tôi không kiểm soát được; khi đó tính năng liên quan có thể gián đoạn.',
          'Bạn có thể ngắt kết nối bất cứ lúc nào trong ứng dụng hoặc thu hồi quyền của ứng dụng trong phần cài đặt của nền tảng đó.',
        ],
      },
      {
        heading: '5. Sử dụng được phép và bị cấm',
        paragraphs: ['Bạn không được:'],
        bullets: [
          'Gửi thư rác, tin nhắn hàng loạt trái quy định hoặc khi chưa có cơ sở hợp pháp để liên hệ khách hàng; vi phạm chính sách nhắn tin của Meta, Zalo hoặc nền tảng khác.',
          'Thu thập, sử dụng dữ liệu cá nhân trái pháp luật; gửi nội dung vi phạm pháp luật, lừa đảo, xúc phạm, xâm phạm quyền của người khác.',
          'Cố ý truy cập trái phép, dò quét lỗ hổng, gây quá tải hoặc làm gián đoạn hệ thống; sao chép, bán lại hoặc dịch ngược phần mềm khi chưa được phép.',
        ],
      },
      {
        heading: '6. Dữ liệu của bạn',
        paragraphs: [
          `Bạn giữ toàn bộ quyền đối với dữ liệu và nội dung mà bạn và khách hàng của bạn đưa vào ${p}. Bạn cấp cho chúng tôi quyền xử lý dữ liệu đó chỉ trong phạm vi cần thiết để cung cấp dịch vụ, theo Chính sách bảo mật. Với dữ liệu khách hàng của bạn, bạn là bên kiểm soát dữ liệu và chịu trách nhiệm về cơ sở pháp lý khi thu thập, sử dụng.`,
        ],
      },
      {
        heading: '7. Tính năng AI',
        paragraphs: [
          'Câu trả lời do AI tạo ra có thể chưa chính xác hoặc chưa phù hợp với mọi tình huống. Bạn chịu trách nhiệm cấu hình, giám sát và kiểm tra nội dung AI gửi tới khách hàng của mình.',
        ],
      },
      {
        heading: '8. Gói dịch vụ và thanh toán',
        paragraphs: [
          'Một số tính năng chỉ có ở gói trả phí. Giá, chu kỳ, hạn mức và điều kiện gia hạn theo gói/báo giá được công bố trong ứng dụng hoặc xác nhận với bạn; khoản đã thanh toán được xử lý theo điều kiện của gói hoặc thoả thuận riêng nếu có.',
        ],
      },
      {
        heading: '9. Sở hữu trí tuệ',
        paragraphs: [
          `Phần mềm, thương hiệu, giao diện và tài liệu của ${p} thuộc quyền của chúng tôi hoặc bên cấp phép. Bạn được cấp quyền sử dụng có giới hạn, không độc quyền, không chuyển nhượng trong thời gian sử dụng dịch vụ.`,
        ],
      },
      {
        heading: '10. Tạm ngừng và chấm dứt',
        paragraphs: [
          'Bạn có thể ngừng sử dụng bất cứ lúc nào. Chúng tôi có thể tạm ngừng hoặc chấm dứt tài khoản vi phạm điều khoản hoặc gây rủi ro cho hệ thống/người khác. Sau khi chấm dứt, dữ liệu được xử lý theo Chính sách bảo mật và Hướng dẫn xoá dữ liệu (/xoa-du-lieu).',
        ],
      },
      {
        heading: '11. Giới hạn trách nhiệm',
        paragraphs: [
          'Dịch vụ được cung cấp trên cơ sở hiện có. Trong phạm vi pháp luật cho phép, chúng tôi không chịu trách nhiệm về thiệt hại gián tiếp, mất lợi nhuận hoặc dữ liệu phát sinh do sự cố của nền tảng bên thứ ba, đường truyền, bất khả kháng hoặc do bạn sử dụng sai điều khoản này. Trách nhiệm tối đa của chúng tôi, nếu có, giới hạn trong khoản phí bạn đã trả cho dịch vụ trong 12 tháng gần nhất trước sự kiện phát sinh.',
        ],
      },
      {
        heading: '12. Luật áp dụng',
        paragraphs: [
          'Điều khoản này chịu sự điều chỉnh của pháp luật Việt Nam. Tranh chấp được ưu tiên giải quyết bằng thương lượng; nếu không được, giải quyết tại Toà án có thẩm quyền tại Việt Nam.',
        ],
      },
      {
        heading: '13. Thay đổi điều khoản',
        paragraphs: [
          'Chúng tôi có thể cập nhật Điều khoản; bản mới được đăng tại trang này kèm ngày hiệu lực. Việc tiếp tục sử dụng sau ngày hiệu lực là chấp nhận bản mới.',
        ],
      },
      { heading: '14. Liên hệ', paragraphs: [contactLine(info)] },
    ],
  }
}

function dataDeletion(info: LegalInfo): LegalDoc {
  const p = info.product
  const mail = info.email ?? 'địa chỉ email liên hệ ở cuối trang'
  return {
    title: 'Hướng dẫn xoá dữ liệu',
    lead: `Cách yêu cầu ${p} xoá dữ liệu có nguồn từ Facebook (Messenger) và thu hồi quyền của ứng dụng.`,
    sections: [
      {
        heading: 'Dữ liệu nào có thể được xoá',
        bullets: [
          'Mã truy cập Trang (Page access token) và thông tin Trang đã kết nối (Page ID, tên Trang).',
          'Dữ liệu người nhắn tin vào Trang: mã PSID, tên hiển thị, ảnh đại diện, nội dung tin nhắn và tệp đính kèm, hồ sơ khách tạo từ các cuộc trò chuyện đó.',
        ],
      },
      {
        heading: 'Cách 1 — Bạn là quản trị viên Trang đã kết nối',
        steps: [
          `Đăng nhập ${p} → Tích hợp → Facebook Messenger → bấm biểu tượng "Ngắt kết nối" ở dòng Trang cần gỡ. Hệ thống ngừng nhận và gửi tin của Trang đó ngay.`,
          'Vào Facebook → Cài đặt → Ứng dụng và trang web (hoặc Cài đặt doanh nghiệp → Tích hợp) → tìm ứng dụng eCDP → Xoá/Thu hồi quyền.',
          `Để xoá hẳn dữ liệu đã lưu (token, hội thoại, hồ sơ khách), gửi yêu cầu tới ${mail} theo mẫu bên dưới. Chúng tôi xác minh bạn là quản trị viên Trang rồi xoá.`,
        ],
      },
      {
        heading: 'Cách 2 — Bạn là người đã nhắn tin vào một Trang dùng eCDP',
        steps: [
          `Gửi email tới ${mail} theo mẫu bên dưới, nêu tên Trang bạn đã nhắn và tên Facebook của bạn.`,
          'Chúng tôi xác định dữ liệu liên quan tới bạn, xoá hoặc ẩn danh hoá, và thông báo kết quả. Bạn cũng có thể gửi yêu cầu cho chính doanh nghiệp sở hữu Trang đó.',
        ],
      },
      {
        heading: 'Mẫu yêu cầu',
        bullets: [
          `Tiêu đề: "Yêu cầu xoá dữ liệu Facebook — ${p}"`,
          'Nội dung: họ tên, tên Trang Facebook (và Page ID nếu có), bạn là quản trị viên Trang hay người nhắn tin, tên Facebook hoặc đường dẫn hồ sơ Facebook của bạn (để chúng tôi tìm đúng dữ liệu).',
        ],
      },
      {
        heading: 'Thời gian xử lý',
        paragraphs: [
          `Chúng tôi xác nhận đã nhận yêu cầu và hoàn tất xoá trong tối đa ${DELETION_SLA_DAYS} ngày kể từ khi xác minh được danh tính. Dữ liệu mà pháp luật bắt buộc phải lưu (ví dụ chứng từ thanh toán) được giữ theo thời hạn luật định và không dùng cho mục đích khác.`,
        ],
      },
      {
        heading: 'Liên hệ',
        paragraphs: [contactLine(info), 'Xem thêm Chính sách bảo mật tại /chinh-sach-bao-mat.'],
      },
    ],
  }
}

function claudeConnector(info: LegalInfo): LegalDoc {
  const p = info.product
  return {
    title: `Kết nối ${p} với Claude`,
    lead: `Dùng Claude (Anthropic) để hỏi đáp, phân tích và soạn nháp trên dữ liệu khách hàng, hội thoại, phân khúc của ${p} — bằng tài khoản Claude của chính bạn. English summary at the end.`,
    sections: [
      {
        heading: '1. Cần gì',
        bullets: [
          `Tài khoản ${p} còn hoạt động. Quản trị viên tổ chức bật "Kết nối AI (Claude)" ở Cài đặt → Kết nối AI (bật cần đồng ý điều khoản chuyển dữ liệu).`,
          'Tài khoản Claude (claude.ai, Claude Desktop, Claude mobile hoặc Claude Code) có hỗ trợ connector. Doanh nghiệp nên dùng gói Claude Team / Enterprise.',
        ],
      },
      {
        heading: '2. Kết nối trên claude.ai / ứng dụng Claude',
        steps: [
          'Mở Claude → Settings → Connectors → "Add custom connector" (hoặc tìm eCDP trong danh bạ connector).',
          `Dán địa chỉ máy chủ MCP: https://api.app.ecdp.vn/mcp`,
          `Claude mở trang đăng nhập ${p} → đăng nhập → xem ứng dụng đang xin quyền gì → bỏ bớt quyền nếu muốn → bấm "Cho phép".`,
        ],
      },
      {
        heading: '3. Kết nối trên Claude Code',
        steps: [
          'Chạy: claude mcp add --transport http ecdp https://api.app.ecdp.vn/mcp',
          `Trong Claude Code gõ /mcp → chọn ecdp → đăng nhập ${p} trên trình duyệt và bấm "Cho phép".`,
        ],
      },
      {
        heading: '4. Claude làm được gì',
        bullets: [
          'Xem (luôn có): tổng quan kinh doanh, tìm khách / hội thoại, hồ sơ khách 360, đọc tin nhắn, phân khúc, thuộc tính, sản phẩm, kho tri thức, nhân viên AI, lịch hẹn, chiến dịch.',
          'Tạo nháp (khi tổ chức cho phép và bạn giữ quyền này lúc kết nối): gợi ý trả lời lưu thành tin NỘI BỘ, ghi chú, gắn nhãn, cập nhật thuộc tính, tạo phân khúc, tri thức chờ duyệt, chiến dịch Zalo ở trạng thái nháp. Claude luôn hỏi bạn trước khi tạo.',
          'Claude KHÔNG gửi tin cho khách, không chạy chiến dịch, không xoá hay xuất dữ liệu.',
          'Prompt dựng sẵn (menu "/"): Báo cáo hôm nay, Báo cáo 7 ngày, Việc cần xử lý, Phân tích một khách, Gợi ý bổ sung tri thức.',
        ],
      },
      {
        heading: '5. Quyền riêng tư và an toàn',
        bullets: [
          `Claude chỉ thấy đúng phần dữ liệu mà bạn được xem trong ${p} (theo vai trò, nhóm, tài khoản kênh được giao).`,
          'Số điện thoại / email của khách bị che (vd 09xxxxx678) trừ khi tổ chức cho phép và bạn giữ quyền "SĐT & email" lúc kết nối.',
          'Mỗi lần Claude dùng eCDP được ghi vào Cài đặt → Nhật ký thao tác (ai, ứng dụng nào, công cụ nào).',
          `Dữ liệu Claude đọc được xử lý theo điều khoản tài khoản Claude của bạn. Chi tiết: ${LEGAL_PATH.privacy}.`,
        ],
      },
      {
        heading: '6. Thu hồi kết nối',
        steps: [
          `${p} → Cài đặt → Kết nối AI → "Kết nối của tôi" → Thu hồi (quản trị viên thu hồi được kết nối của mọi người, hoặc tắt hẳn Kết nối AI cho tổ chức).`,
          'Hoặc gỡ connector eCDP trong Claude (Settings → Connectors).',
        ],
      },
      {
        heading: '7. Hỗ trợ',
        paragraphs: [contactLine(info)],
      },
      {
        heading: 'English summary',
        paragraphs: [
          `${p} is a CRM / customer data platform. Its MCP connector lets Claude read customers, conversations, segments, products and knowledge base entries, and — only when the organization allows it — create drafts (internal reply suggestions, notes, tags, properties, segments, knowledge entries pending approval, draft Zalo campaigns). It never sends messages to customers, runs campaigns, deletes or exports data.`,
          'Setup: an organization admin enables "Kết nối AI (Claude)" in Settings; each user adds https://api.app.ecdp.vn/mcp as a connector in Claude and signs in with OAuth 2.1 (PKCE). Access is limited to what that user can see in eCDP; phone numbers and emails are masked unless allowed. Every call is audit-logged; connections can be revoked at any time in Settings → Kết nối AI.',
        ],
      },
    ],
  }
}
